import {
  BusOutline18Icon,
  MaxitaxiOutline18Icon,
  MinibussOutline18Icon,
  PhoneOutline24Icon,
  QuestionOutline18Icon,
  TaxiOutline18Icon,
} from "@vygruppen/spor-icon-react";
import { getVehicleLabel } from "features/VehicleSidebar/VehicleList/AlternativeTransportList/getVehicleLabel";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { CallButton } from "shared/components/buttons/CallButton";
import { TimeInterval } from "shared/components/dataDisplay/TimeInterval";
import { DropsButton } from "shared/components/Spor";
import { Text } from "shared/components/typography/TitleStyles";
import {
  AlternativeTransportLog,
  VehicleStatus,
  VehicleType,
} from "shared/types/alternativeTransport";
import styled from "styled-components";

type VehicleSummaryProps = {
  vehicleInfo?: string | null;
  title: string;
  type: VehicleType;
  subTitle?: string | null;
  route: string;
  startTime: Date | null;
  endTime: Date | null;
  date?: string | null;
  vehicleStatus: VehicleStatus | null;
  logs: AlternativeTransportLog[];
  driverPhoneNumber?: string | null;
};

const VehicleSummaryWrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding-bottom: 3px;
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 6px;
  padding-bottom: 3px;

  svg {
    margin-right: 0.5rem;
  }
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
  padding-bottom: 6px;
`;

const getPartialCancelledStatusFromLogs = (
  vehicleStatus: VehicleStatus | null,
  logs: AlternativeTransportLog[],
) => {
  if (vehicleStatus === "CANCELLED") {
    return "CANCELLED";
  }

  const sortedLogs = [...logs].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
  );

  const isPartiallyCancelled = sortedLogs.reduce((state, log) => {
    const message = log.message?.toLowerCase() || "";

    if (message.includes("kanselleringen er trukket tilbake")) {
      return false;
    }

    if (message.includes("kansellert")) {
      return true;
    }

    return state;
  }, false);

  return isPartiallyCancelled ? "PARTIALLY_CANCELLED" : null;
};

export const VehicleSummary: FC<VehicleSummaryProps> = ({
  vehicleInfo,
  title,
  type,
  subTitle,
  route,
  startTime,
  endTime,
  date,
  vehicleStatus,
  logs,
  driverPhoneNumber,
}) => {
  const phoneNumberWithDigitsOnly = driverPhoneNumber
    ? driverPhoneNumber.replace("+", "00")
    : null;

  const { t } = useTranslation();

  const getTransportTypeIconAndText = (vehicleType: VehicleType) => {
    switch (vehicleType) {
      case "BUS":
        return { Icon: BusOutline18Icon, text: t("Buss") };
      case "MINIBUS":
        return { Icon: MinibussOutline18Icon, text: t("Minibuss") };
      case "TAXI":
        return { Icon: TaxiOutline18Icon, text: t("Taxi") };
      case "MAXITAXI":
        return { Icon: MaxitaxiOutline18Icon, text: t("MaxiTaxi") };
      case "UNKNOWN":
        return { Icon: QuestionOutline18Icon, text: t("Ukjent kjøretøy") };
    }
  };

  const { Icon: TypeIcon, text: typeText } = getTransportTypeIconAndText(type);

  return (
    <VehicleSummaryWrapper>
      <Header>
        <Text bold>
          {getVehicleLabel(
            getPartialCancelledStatusFromLogs(vehicleStatus, logs),
          )}
        </Text>
        {vehicleInfo && <Text secondary>{vehicleInfo}</Text>}
      </Header>
      <Row>
        {title.length < 16 ? (
          <>
            <Text
              bold
              style={{
                display: "block",
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap",
                maxWidth: "16rem",
              }}
            >
              {title}
            </Text>
            {subTitle && (
              <Text title="Rutedetaljer" secondary>
                {subTitle}
              </Text>
            )}
          </>
        ) : (
          <Text
            bold
            style={{
              display: "block",
              textOverflow: "ellipsis",
              overflow: "hidden",
              whiteSpace: "nowrap",
              maxWidth: "10rem",
            }}
          >
            {title}
          </Text>
        )}
      </Row>
      <Row>
        <Text title="Planlagt rute">{route}</Text>
      </Row>
      <Row>
        <TypeIcon style={{ display: "block" }} />
        <Text title={typeText}>{typeText}</Text>
      </Row>
      <Row>
        <TimeInterval startTime={startTime} endTime={endTime} />
      </Row>

      <Row>
        {date && (
          <Text title="Dato for avgang" secondary>
            {date}
          </Text>
        )}
      </Row>
      {!driverPhoneNumber ? (
        <DropsButton
          leftIcon={<PhoneOutline24Icon />}
          variant="tertiary"
          disabled={!driverPhoneNumber}
          size="sm"
        >
          Ikke registrert
        </DropsButton>
      ) : (
        <CallButton phoneNumber={phoneNumberWithDigitsOnly!} size="sm" />
      )}
    </VehicleSummaryWrapper>
  );
};
